import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "react-query";
import { useRadioidByNameseo, useUser } from "../../hooks/useApi";
import {
  getTermsExtraValue,
  isTermsAcceptedValue,
} from "../../helpers/termsAcceptance";
import { EVENT_TYPES, reportEvent } from "../../services/eventEmitter";
import { getBrowserSessionId } from "../../services/eventEmitter/storage";
import { patchEntryIntent, getEntryIntent } from "./entryIntentStorage";
import { fetchConsent, fetchTermsDefinition, persistConsent } from "./termsApi";
import {
  clearTermsDraft,
  forgetConsent,
  hasRememberedConsent,
  readTermsDraft,
  rememberConsent,
  saveTermsDraft,
} from "./termsStorage";
import { getCommunicationConfig, buildTermsEvidence } from "./communicationConsent";
import TermsAcceptanceForm, { parseTermsForm } from "./TermsAcceptanceForm";

// A module-level in-flight registry prevents remounts from submitting the same
// explicit interaction twice. It contains promises only while work is running.
const submissions = new Map();
const warn = (stage) =>
  console.warn("MBX-TERMS: continuing after terms error", stage);

export default function useEntryTerms({
  nameseo,
  intent,
  enabled,
  continuation,
  onIntentChange,
}) {
  const { logged, user, token: sessionToken } = useUser();
  const token = user?.token || sessionToken;
  const userid = logged ? user?.userid : null;
  const radio = useRadioidByNameseo(enabled ? nameseo : null);
  const definition = useQuery(
    ["entry-terms", nameseo],
    () => fetchTermsDefinition(nameseo),
    {
      enabled: !!enabled && !!nameseo,
      retry: false,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  );
  const parsed = useMemo(() => {
    try {
      const fields = definition.data ? parseTermsForm(definition.data.text) : null;
      if (definition.data) getCommunicationConfig(definition.data, fields);
      return { fields };
    } catch (_) {
      return { error: true };
    }
  }, [definition.data]);
  const scope = definition.data?.termsScope;
  const radioid = radio.radioid;
  const key = `${intent?.id}:${nameseo}:${logged ? `${userid}:${token}` : "anonymous"}`;
  const [outcome, setOutcome] = useState(null);
  const [draftRevision, setDraftRevision] = useState(0);
  const epoch = useRef(0);
  const context = useMemo(
    () => ({ nameseo, radioid, scope, userid, intentId: intent?.id }),
    [nameseo, radioid, scope, userid, intent?.id],
  );
  const accepted = (data) =>
    isTermsAcceptedValue(getTermsExtraValue(data), {
      hasCustomForm: !!parsed.fields,
    });

  useEffect(() => {
    const run = ++epoch.current;
    let disposed = false;
    const current = () => !disposed && run === epoch.current;
    const finish = (status) => {
      if (current()) setOutcome({ key, status });
    };
    if (!enabled || !intent || !nameseo) return;
    // Carry a fail-open decision only across the immediate internal navigation
    // from redeem/task to this same account and radio, never another account.
    if (
      logged &&
      userid != null &&
      continuation?.nameseo === nameseo &&
      String(continuation.userid) === String(userid) &&
      continuation.expiresAt > Date.now() &&
      continuation.status === "error"
    ) {
      // Consume navigation state so a reload/new visit checks again.
      const historyState = { ...window.history.state };
      delete historyState.termsContinuation;
      window.history.replaceState(historyState, "");
      finish("error");
      return;
    }
    if (definition.isError || parsed.error || radio.isError) {
      warn("load");
      finish("error");
      return;
    }
    if (definition.isLoading || !definition.isSuccess) return;
    if (!definition.data) {
      finish("none");
      return;
    }
    if (!radioid) return;
    if (!logged) {
      const draft = readTermsDraft(context);
      finish(draft || hasRememberedConsent(context) ? "login" : "form");
      return;
    }
    // React 17 can briefly expose logged=true before user/token propagation.
    if (userid == null || !token) return;
    setOutcome({ key, status: "checking" });
    const ids = Array.from(
      new Set([radioid, scope?.referenceRadioid].filter(Boolean).map(String)),
    );
    const read = () =>
      Promise.allSettled(ids.map((id) => fetchConsent({ radioid: id, token })));
    const consentIn = (results) =>
      results.some(
        (result) => result.status === "fulfilled" && accepted(result.value),
      );
    const verify = async () => {
      const initial = await read();
      if (!current()) return;
      if (consentIn(initial)) {
        rememberConsent(context);
        clearTermsDraft();
        finish("accepted");
        // Migration is not a new acceptance: fill only successfully read,
        // missing sides and never report another confirmation event.
        initial.forEach((result, index) => {
          if (result.status === "fulfilled" && !accepted(result.value)) {
            void persistConsent({
              radioid: ids[index],
              token,
              extra: ["terms"],
            }).catch(() => warn("sync"));
          }
        });
        return;
      }
      if (initial.some((result) => result.status === "rejected")) {
        warn("read");
        finish("error");
        return;
      }
      forgetConsent(context);
      const draft = readTermsDraft(context);
      if (
        !draft ||
        (draft.userid != null && String(draft.userid) !== String(userid))
      ) {
        finish("form");
        return;
      }
      saveTermsDraft({ ...draft, userid });
      const submissionKey = `${draft.interactionId}:${userid}`;
      if (!submissions.has(submissionKey)) {
        const submission = (async () => {
          const writes = await Promise.allSettled(
            ids.map((id) =>
              persistConsent({ radioid: id, token, extra: draft.extra }),
            ),
          );
          const verified = await read();
          const confirmed = consentIn(verified);
          const response = writes.find(
            (result) => result.status === "fulfilled",
          )?.value;
          if (confirmed) {
            rememberConsent(context);
            if (response) {
              // Complete this interaction even if its screen unmounted while
              // saving. The account snapshot is the one bound before writes.
              void reportEvent({
                type: draft.isCustomForm
                  ? EVENT_TYPES.CUSTOM_TERMS_CONFIRMED
                  : EVENT_TYPES.TERMS_CONFIRMED,
                user,
                radio: { radioid, radioname: radio.data?.name, nameseo },
                occurredAt: draft.acceptedAt,
                idempotencyKey: `terms:${submissionKey}`,
                payload: {
                  ...draft.browserMetadata,
                  ...buildTermsEvidence(draft, user),
                  selectedOptions: draft.extra,
                  form: draft.formMetadata,
                  values: draft.formValues,
                  response,
                  interactionSource: "terms_modal",
                  acceptedAt: draft.acceptedAt,
                },
              }).catch(() => warn("event"));
            }
          } else warn("write");
          clearTermsDraft(draft.interactionId);
          return confirmed;
        })();
        submissions.set(submissionKey, submission);
        void submission
          .finally(() => submissions.delete(submissionKey))
          .catch(() => {});
      }
      const confirmed = await submissions.get(submissionKey);
      if (current()) finish(confirmed ? "accepted" : "error");
    };
    void verify().catch(() => {
      if (current()) {
        warn("verify");
        finish("error");
      }
    });
    return () => {
      disposed = true;
    };
    // The key tracks the account/session and target. Avoid restarting on
    // unrelated user updates (e.g. points) or a fresh radio object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    key,
    definition.isSuccess,
    definition.isError,
    definition.data,
    radioid,
    radio.isError,
    parsed.error,
    draftRevision,
  ]);

  const state = outcome?.key === key ? outcome.status : "checking";
  const ready =
    !!enabled && logged && ["accepted", "none", "error"].includes(state);
  const accept = (data) => {
    const activeIntent = getEntryIntent() || intent;
    if (activeIntent.id !== intent.id) onIntentChange(activeIntent);
    const acceptedAt = new Date().toISOString();
    const interactionId =
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    saveTermsDraft({
      ...context,
      intentId: activeIntent.id,
      userid: logged ? userid : null,
      ...data,
      browserMetadata: {
        landingUrl: window.location.href,
        userAgent: window.navigator.userAgent,
        sessionId: getBrowserSessionId(),
      },
      acceptedAt,
      interactionId,
      expiresAt: activeIntent.expiresAt,
    });
    setDraftRevision((revision) => revision + 1);
  };
  useEffect(() => {
    if (enabled && intent)
      patchEntryIntent({
        terms: { required: !!definition.data, status: state },
      });
  }, [enabled, intent, state, definition.data]);
  const getContinuation = useCallback(
    () => ({ nameseo, userid, status: state, expiresAt: Date.now() + 60000 }),
    [nameseo, userid, state],
  );
  return {
    ready,
    loading: state === "checking",
    loginAllowed: !logged && ["login", "none", "error"].includes(state),
    screen:
      state === "form" ? (
        <TermsAcceptanceForm
          key={key}
          definition={definition.data}
          fields={parsed.fields}
          nameseo={nameseo}
          radioid={radioid}
          radioName={radio.data?.name}
          onAccept={accept}
        />
      ) : null,
    getContinuation,
  };
}
